import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { api } from '../services/api';
import * as Recharts from 'recharts';
import { Package, Warehouse, DollarSign, AlertTriangle, ClipboardList } from 'lucide-react';
import { io } from 'socket.io-client';
import { useAuth } from '../AuthContext';

const { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, PieChart, Pie, Cell } = Recharts;

const Dashboard = () => {
    const { user } = useAuth();
    const [metrics, setMetrics] = useState(null);
    const [trends, setTrends] = useState([]);
    const [clients, setClients] = useState([]);
    const [loading, setLoading] = useState(true);
    const [trendDays, setTrendDays] = useState(30);
    const [warehouseFilter, setWarehouseFilter] = useState('All');
    const [clientOnHandData, setClientOnHandData] = useState([]);
    const [clientMovementData, setClientMovementData] = useState([]);
    const [viewType, setViewType] = useState('movement'); // Default to movement (SKU Activity)

    // Process data to avoid zero values for log scale
    const processedClientOnHandData = useMemo(() => clientOnHandData.map(d => ({ ...d, onHand: d.onHand && d.onHand > 0 ? d.onHand : 1 })), [clientOnHandData]);
    const processedClientMovementData = useMemo(() => clientMovementData.map(d => ({ ...d, activeSkus: d.activeSkus && d.activeSkus > 0 ? d.activeSkus : 1 })), [clientMovementData]);
    const pieData = useMemo(() => {
        if (!metrics?.inventorySummary?.byWarehouse) return [];
        return metrics.inventorySummary.byWarehouse.map(wh => ({
            name: wh.warehouseName,
            value: wh.onHand
        }));
    }, [metrics]);

    const finalPieData = useMemo(() => {
        return pieData
            .filter(v => v.value > 0)
            .sort((a, b) => b.value - a.value);
    }, [pieData]);

    const pieTotal = useMemo(() => finalPieData.reduce((sum, d) => sum + d.value, 0), [finalPieData]);
    // Check if user is warehouse-restricted (manager or picker)
    const isWarehouseRestricted = user && (user.role === 'manager' || user.role === 'picker') && user.warehouse;
    const userWarehouse = user?.warehouse;

    // Ref always points to the latest fetchDashboardData — lets the socket (created once)
    // call the current version without capturing a stale closure.
    const fetchRef = useRef(null);

    const fetchDashboardData = useCallback(async () => {
        try {
            const metricsRes = await api.inventory.getDashboardMetrics().catch(() => null);
            const trendsRes = await api.inventory.getTrends(trendDays).catch(() => null);

            if (metricsRes?.data) {
                let metricsData = metricsRes.data;

                // Filter metrics for warehouse-restricted users
                if (isWarehouseRestricted && metricsData.inventorySummary?.byWarehouse) {
                    const filteredWarehouses = metricsData.inventorySummary.byWarehouse.filter(
                        wh => wh.warehouseName === userWarehouse
                    );
                    const filteredOnHand = filteredWarehouses.reduce((sum, wh) => sum + wh.onHand, 0);
                    metricsData = {
                        ...metricsData,
                        totalWarehouses: filteredWarehouses.length || 1,
                        inventorySummary: {
                            ...metricsData.inventorySummary,
                            onHand: filteredOnHand,
                            byWarehouse: filteredWarehouses
                        }
                    };
                }

                setMetrics(metricsData);
            }

            if (trendsRes?.data) {
                setTrends(trendsRes.data.trends || []);
                setClients(trendsRes.data.clients || []);
            }
        } catch (error) {
            console.error("Dashboard data error", error);
        } finally {
            setLoading(false);
        }
    }, [trendDays, isWarehouseRestricted, userWarehouse]);

    // Always keep the ref pointing to the latest fetch function
    useEffect(() => {
        fetchRef.current = fetchDashboardData;
    }, [fetchDashboardData]);

    // Fetch client metrics whenever warehouse filter or trendDays changes
    useEffect(() => {
        const fetchClientData = async () => {
            try {
                const [onHandRes, movementRes] = await Promise.all([
                    api.inventory.getClientOnHand(warehouseFilter),
                    api.inventory.getClientMovement(trendDays, warehouseFilter)
                ]);

                if (onHandRes?.data?.data) setClientOnHandData(onHandRes.data.data);
                if (movementRes?.data?.data) setClientMovementData(movementRes.data.data);
            } catch (e) {
                console.error('Error fetching client data', e);
            }
        };
        fetchClientData();
    }, [warehouseFilter, trendDays]);

    // Effect 1: Re-fetch data whenever trendDays or user context changes
    useEffect(() => {
        fetchDashboardData();
    }, [fetchDashboardData]);

    // Effect 2: Create the socket ONCE and never tear it down unnecessarily.
    // Uses fetchRef.current so listeners always call the latest version of the fetch function.
    useEffect(() => {
        const socketUrl = (process.env.REACT_APP_API_URL || 'http://localhost:8009/api').replace('/api', '');
        const socket = io(socketUrl, {
            reconnectionAttempts: 10,
            reconnectionDelay: 2000,
        });

        const handleUpdate = (eventName) => (data) => {
            console.log(`[Dashboard Socket] ${eventName}:`, data);
            if (fetchRef.current) fetchRef.current();
        };

        socket.on('connect', () => console.log('[Dashboard Socket] Connected'));
        socket.on('disconnect', (r) => console.warn('[Dashboard Socket] Disconnected:', r));
        socket.on('connect_error', (e) => console.error('[Dashboard Socket] Error:', e.message));

        socket.on('inventory_updated', handleUpdate('inventory_updated'));
        socket.on('forecast_updated', handleUpdate('forecast_updated'));
        socket.on('po_updated', handleUpdate('po_updated'));
        socket.on('bin_inventory_updated', handleUpdate('bin_inventory_updated'));
        socket.on('assignment_updated', handleUpdate('assignment_updated'));

        // 30-second polling fallback in case a webhook is missed
        const pollInterval = setInterval(() => {
            if (fetchRef.current) fetchRef.current();
        }, 30000);

        return () => {
            socket.disconnect();
            clearInterval(pollInterval);
        };
    }, []); // <-- Empty: socket lifecycle is independent of data dependencies

    const COLORS = [
        '#6366f1', // Indigo
        '#3b82f6', // Blue
        '#06b6d4', // Cyan
        '#10b981', // Emerald
        '#84cc16', // Lime
        '#f59e0b', // Amber
        '#f97316', // Orange
        '#ef4444', // Red
        '#ec4899', // Pink
        '#8b5cf6', // Violet
        '#64748b'  // Slate (for "Others")
    ];

    if (loading) return <div className="p-8">Loading dashboard analytics...</div>;

    return (
        <div>
            <div className="page-header">
                <div>
                    <h1>Executive Overview</h1>
                    <p>
                        {isWarehouseRestricted ? (
                            <>Warehouse: <strong style={{ color: 'var(--primary)' }}>{userWarehouse}</strong> — Real-time inventory for your location.</>
                        ) : (
                            'Real-time client inventory distribution and trends.'
                        )}
                    </p>
                </div>
            </div>

            <div className="metrics-grid">
                <div className="card metric-card" style={{ borderLeft: '4px solid #6366f1' }}>
                    <div className="metric-icon" style={{ backgroundColor: 'rgba(99, 102, 241, 0.1)', color: '#6366f1' }}>
                        <Package size={28} />
                    </div>
                    <div className="metric-content">
                        <p>Total Products (SKUs)</p>
                        <h3>{metrics?.totalProducts?.toLocaleString() || 0}</h3>
                    </div>
                </div>

                <div className="card metric-card" style={{ borderLeft: '4px solid #8b5cf6' }}>
                    <div className="metric-icon" style={{ backgroundColor: 'rgba(139, 92, 246, 0.1)', color: '#8b5cf6' }}>
                        <Warehouse size={28} />
                    </div>
                    <div className="metric-content">
                        <p>{isWarehouseRestricted ? 'Your Warehouse' : 'Active Warehouses'}</p>
                        <h3>{isWarehouseRestricted ? userWarehouse : (metrics?.totalWarehouses?.toLocaleString() || 0)}</h3>
                    </div>
                </div>

                <div className="card metric-card" style={{ borderLeft: '4px solid #10b981' }}>
                    <div className="metric-icon" style={{ backgroundColor: 'rgba(16, 185, 129, 0.1)', color: '#10b981' }}>
                        <DollarSign size={28} />
                    </div>
                    <div className="metric-content">
                        <p>Total On-Hand Availability</p>
                        <h3>{metrics?.inventorySummary?.onHand?.toLocaleString() || 0}</h3>
                    </div>
                </div>

                {metrics?.inventorySummary?.byWarehouse?.map((wh, idx) => (
                    <div key={idx} className="card metric-card" style={{ borderLeft: '4px solid #3b82f6' }}>
                        <div className="metric-icon" style={{ backgroundColor: 'rgba(59, 130, 246, 0.1)', color: '#3b82f6' }}>
                            <Warehouse size={28} />
                        </div>
                        <div className="metric-content">
                            <p>On-Hand: {wh.warehouseName}</p>
                            <h3>{wh.onHand.toLocaleString()}</h3>
                        </div>
                    </div>
                ))}

                <div className="card metric-card" style={{ borderLeft: '4px solid #ef4444' }}>
                    <div className="metric-icon" style={{ backgroundColor: 'rgba(239, 68, 68, 0.1)', color: '#ef4444' }}>
                        <AlertTriangle size={28} />
                    </div>
                    <div className="metric-content">
                        <p>Out of Stock Products</p>
                        <h3 style={{ color: '#ef4444' }}>{metrics?.outOfStockCount || 0}</h3>
                    </div>
                </div>

                <div className="card metric-card" style={{ borderLeft: '4px solid #f97316' }}>
                    <div className="metric-icon" style={{ backgroundColor: 'rgba(249, 115, 22, 0.1)', color: '#f97316' }}>
                        <AlertTriangle size={28} />
                    </div>
                    <div className="metric-content">
                        <p>Warning (Needs Replenishment)</p>
                        <h3 style={{ color: '#f97316' }}>{metrics?.warningCount || 0}</h3>
                    </div>
                </div>

                <div className="card metric-card" style={{ borderLeft: '4px solid var(--accent)' }}>
                    <div className="metric-icon" style={{ backgroundColor: 'rgba(253, 206, 6, 0.15)', color: 'var(--accent)' }}>
                        <ClipboardList size={28} />
                    </div>
                    <div className="metric-content">
                        <p>Active Inbound POs</p>
                        <h3>{metrics?.activePOs || 0}</h3>
                    </div>
                </div>
            </div>

            <div className="charts-grid">
                <div className="card" style={{ marginBottom: 0 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                        <h2 className="card-title" style={{ margin: 0 }}>
                            {viewType === 'movement' ? 'Client SKU Activity' : 'Client Inventory Levels'} ({trendDays} Days)
                        </h2>
                        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                            <div style={{ display: 'flex', background: '#f1f5f9', padding: '0.25rem', borderRadius: '8px', marginRight: '1rem' }}>
                                <button
                                    onClick={() => setViewType('inventory')}
                                    style={{ padding: '0.4rem 0.8rem', borderRadius: '6px', border: 'none', background: viewType === 'inventory' ? 'white' : 'transparent', boxShadow: viewType === 'inventory' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600, color: viewType === 'inventory' ? 'var(--primary)' : 'var(--text-muted)' }}
                                >
                                    On-Hand
                                </button>
                                <button
                                    onClick={() => setViewType('movement')}
                                    style={{ padding: '0.4rem 0.8rem', borderRadius: '6px', border: 'none', background: viewType === 'movement' ? 'white' : 'transparent', boxShadow: viewType === 'movement' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600, color: viewType === 'movement' ? 'var(--primary)' : 'var(--text-muted)' }}
                                >
                                    SKU Activity
                                </button>
                            </div>
                            {[3, 7, 15, 30].map(days => (
                                <button
                                    key={days}
                                    onClick={() => setTrendDays(days)}
                                    style={{
                                        padding: '0.4rem 0.8rem',
                                        borderRadius: '6px',
                                        border: '1px solid var(--primary)',
                                        background: trendDays === days ? 'var(--primary)' : 'transparent',
                                        color: trendDays === days ? 'white' : 'var(--primary)',
                                        cursor: 'pointer',
                                        fontSize: '0.875rem',
                                        fontWeight: '500',
                                        transition: 'all 0.2s'
                                    }}
                                >
                                    {days} Days
                                </button>
                            ))}
                        </div>
                    </div>
                    {/* Warehouse Filter — outside chart area */}
                    {!isWarehouseRestricted && (
                        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '1rem', gap: '0.75rem' }}>
                            <label htmlFor="warehouseFilter" style={{ color: 'var(--text-muted)', fontSize: '0.9rem', fontWeight: 500, whiteSpace: 'nowrap' }}>Warehouse:</label>
                            <select id="warehouseFilter" value={warehouseFilter} onChange={e => setWarehouseFilter(e.target.value)} style={{ padding: '0.3rem 0.7rem', borderRadius: '6px', border: '1px solid var(--primary)', background: 'transparent', color: 'var(--text-main)', cursor: 'pointer' }}>
                                <option value="All">All Warehouses</option>
                                {metrics?.inventorySummary?.byWarehouse?.map((wh, idx) => (
                                    <option key={idx} value={wh.warehouseName}>{wh.warehouseName}</option>
                                ))}
                            </select>
                        </div>
                    )}
                    <div style={{ width: '100%', height: Math.max(400, ((viewType === 'inventory' ? clientOnHandData : clientMovementData).length * 40) + 60) }}>
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart
                                layout="vertical"
                                data={viewType === 'inventory' ? processedClientOnHandData : processedClientMovementData}
                                margin={{ top: 5, right: 30, left: 10, bottom: 5 }}
                            >
                                <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                                <XAxis type="number" scale="log" domain={[0.1, 'auto']} tickFormatter={value => value >= 1000 ? `${(value / 1000).toFixed(1)}k` : value.toLocaleString()} tick={{ fontSize: 12 }} />
                                <YAxis
                                    dataKey="clientName"
                                    type="category"
                                    width={180}
                                    tick={({ x, y, payload }) => (
                                        <g transform={`translate(${x},${y})`}>
                                            <text
                                                x={0}
                                                y={0}
                                                dy={4}
                                                textAnchor="end"
                                                fill="var(--text-main)"
                                                fontSize={12}
                                            >
                                                {payload.value.length > 22 ? payload.value.substring(0, 20) + '…' : payload.value}
                                            </text>
                                        </g>
                                    )}
                                />
                                <Tooltip
                                    formatter={(value, name) => [value.toLocaleString(), name]}
                                    contentStyle={{ borderRadius: '8px', fontSize: '13px' }}
                                />
                                {viewType === 'inventory' ? (
                                    <Bar dataKey="onHand" name="On-Hand Units" fill="#3b82f6" radius={[0, 4, 4, 0]} barSize={20} />
                                ) : (
                                    <Bar dataKey="activeSkus" name="Active SKUs" fill="#10b981" radius={[0, 4, 4, 0]} barSize={20} />
                                )}
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                <div className="card" style={{ marginBottom: 0 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                        <h2 className="card-title" style={{ margin: 0 }}>Warehouse Distribution</h2>
                    </div>
                    <div style={{ width: '100%', height: 350, position: 'relative' }}>
                        {finalPieData.length > 0 ? (
                            <ResponsiveContainer>
                                <PieChart>
                                    <Pie
                                        data={finalPieData}
                                        cx="50%"
                                        cy="50%"
                                        innerRadius={80}
                                        outerRadius={110}
                                        paddingAngle={5}
                                        dataKey="value"
                                        stroke="none"
                                    >
                                        {finalPieData.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                        ))}
                                    </Pie>
                                    <Tooltip
                                        formatter={(value) => value.toLocaleString()}
                                        contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 8px 16px rgba(0,0,0,0.15)', padding: '10px 15px' }}
                                    />
                                </PieChart>
                            </ResponsiveContainer>
                        ) : (
                            <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)' }}>
                                No data available for this selection
                            </div>
                        )}
                        {finalPieData.length > 0 && (
                            <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', textAlign: 'center', pointerEvents: 'none' }}>
                                <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Total</div>
                                <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--text-main)' }}>{pieTotal.toLocaleString()}</div>
                            </div>
                        )}
                    </div>
                    <div style={{ marginTop: '1.5rem', borderTop: '1px solid #f1f5f9', paddingTop: '1.5rem' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '0.75rem' }}>
                            {finalPieData.slice(0, 12).map((entry, i) => {
                                const pct = pieTotal > 0 ? ((entry.value / pieTotal) * 100).toFixed(1) : 0;
                                return (
                                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#f8fafc', padding: '0.6rem 0.8rem', borderRadius: '10px', minWidth: 0, border: '1px solid #f1f5f9' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', minWidth: 0, overflow: 'hidden' }}>
                                            <div style={{ width: 10, height: 10, borderRadius: '2px', backgroundColor: COLORS[i % COLORS.length], flexShrink: 0 }}></div>
                                            <span style={{ fontSize: '0.8rem', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'var(--text-main)' }} title={entry.name}>
                                                {entry.name}
                                            </span>
                                        </div>
                                        <span style={{ fontSize: '0.8rem', color: 'var(--primary)', fontWeight: 700, flexShrink: 0, marginLeft: '0.5rem' }}>
                                            {pct}%
                                        </span>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Dashboard;
